//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import { Redirect, useLocation } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { logEvent } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import { ANALYTICS_EVENT_EMAIL_VERIFIED, ANALYTICS_EVENT_PASSWORD_RESET, BASE_URL } from '../common/constants';
import { AUTH_MODE_RESET_PASSWORD, AUTH_MODE_VERIFY_EMAIL, PATH_HOME } from '../system/constants';
import { applyActionCode, resetPassword, verifyPasswordResetCode } from '../common/services/authServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdPageHead from '../common/elemental/AfitdPageHead';
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import TopNav from '../topNav/TopNav';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import Processing from './subComponents/Processing';
import ActionCodeFailed from './subComponents/ActionCodeFailed';
import ResetPasswordForm from './subComponents/ResetPasswordForm';
import PageMessage from '../common/elemental/PageMessage';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    pageHeader: {
        maxHeight: '81px',
        // [theme.breakpoints.down('md')]: { maxHeight: '134px' }, // commented out because no more second row menu -el 20221022
        // [theme.breakpoints.down('sm')]: { maxHeight: '81px' },
    },
    pageContent: {
        flex: 1,
        overflowY: 'hidden',
    },
    tempContainer: { alignItems: 'center', marginTop: 50 },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// A custom hook that builds on useLocation to parse the query string for you.
const useQuery = () => {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
};

const PAGE_ID_VERIFYING_EMAIL = 1;
const PAGE_ID_EMAIL_VERIFIED_SUCCESSFUL = 2;
const PAGE_ID_RESEND_VERIFICATION_EMAIL = 3;
const PAGE_ID_ACTION_CODE_FAILED_VERIFY_EMAIL = 4;
const PAGE_ID_ACTION_CODE_FAILED_EMAIL_VERIFICATION = 5;
const PAGE_ID_ACTION_CODE_FAILED_VERIFIED_PWD_RESET_CODE = 6;
const PAGE_ID_RESET_PASSWORD_FORM = 7;
const PAGE_ID_RESET_PASSWORD_SUCCESSFUL = 8;

const PAGE_ID_REDIRECT_TO_HOME_PAGE = 99;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AuthHandlerLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const appContext = useContext(AppContext);
    const { auth, isAuthenticationPending, authUser, emailVerified } = appContext;
    const context = useContext(LayoutContext);
    // const { setLoadingDialogOpen } = context;
    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    const query = useQuery();
    const mode = query.get('mode');
    const oobCode = query.get('oobCode');

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { showVerificationEmailSent } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [currentPageId, setCurrentPageId] = useState();
    const [isCodeApplied, setIsCodeApplied] = useState();
    const [isPwdResetCodeVerified, setIsPwdResetCodeVerified] = useState();
    const [isRequestOpenLoginDialog, setIsRequestOpenLoginDialog] = useState();
    const [isRequestLogOut, setIsRequestLogOut] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const applyAction = async () => {
            setCurrentPageId(PAGE_ID_VERIFYING_EMAIL);
            try {
                await applyActionCode({ code: oobCode });

                // applied code successfully
                setIsCodeApplied(true);

                logEvent(ANALYTICS_EVENT_EMAIL_VERIFIED);

                switch (mode) {
                    //---------------------------------------------------------------------
                    // Verify Email
                    //---------------------------------------------------------------------
                    case AUTH_MODE_VERIFY_EMAIL: {
                        // if user has signed in, reload auth token (????????)
                        if (authUser) {
                            authUser.reload();
                        }
                        setCurrentPageId(PAGE_ID_EMAIL_VERIFIED_SUCCESSFUL);
                        setIsRequestLogOut(true);
                        break;
                    }
                    default:
                        // do nothing
                        break;
                }
            } catch (error) {
                // apply code failed
                console.log(`Error received: ${error.displayMessage}`);
                switch (mode) {
                    //---------------------------------------------------------------------
                    // Verify Email
                    //---------------------------------------------------------------------
                    case AUTH_MODE_VERIFY_EMAIL: {
                        if (emailVerified) {
                            setCurrentPageId(PAGE_ID_ACTION_CODE_FAILED_EMAIL_VERIFICATION);
                        } else {
                            setCurrentPageId(PAGE_ID_ACTION_CODE_FAILED_VERIFY_EMAIL);
                        }
                        break;
                    }
                    default:
                        //do nothing
                        break;
                }
            }
        };

        const verifyPwdResetCode = async () => {
            try {
                if (!isPwdResetCodeVerified) {
                    await verifyPasswordResetCode({ code: oobCode });

                    // verified code successfully
                    setIsPwdResetCodeVerified(true);
                } else {
                    setCurrentPageId(PAGE_ID_RESET_PASSWORD_FORM);
                }
            } catch (error) {
                // apply code failed
                console.log(`Error received: ${error.displayMessage}`);
                setCurrentPageId(PAGE_ID_ACTION_CODE_FAILED_VERIFIED_PWD_RESET_CODE);
            }
        };

        if (mode) {
            switch (mode) {
                //---------------------------------------------------------------------
                // Verify Email
                //---------------------------------------------------------------------
                case AUTH_MODE_VERIFY_EMAIL: {
                    if (!isAuthenticationPending && !isCodeApplied) {
                        applyAction();
                    }
                    break;
                }
                case AUTH_MODE_RESET_PASSWORD: {
                    verifyPwdResetCode();
                    break;
                }
                default:
                    // do nothing
                    break;
            }
        } else {
            if (!authUser) {
                // user not authenticated (logged out?) and 'mode' is not provided, redirect to home page.
                setCurrentPageId(PAGE_ID_REDIRECT_TO_HOME_PAGE);
            } else {
                if (showVerificationEmailSent) {
                    setCurrentPageId(PAGE_ID_RESEND_VERIFICATION_EMAIL);
                }
            }
        }
    }, [isAuthenticationPending, authUser, mode, oobCode, emailVerified, showVerificationEmailSent, isCodeApplied, isPwdResetCodeVerified, setIsRequestLogOut]);

    useEffect(() => {
        if (isRequestLogOut) {
            if (auth && authUser) {
                auth.signOut();
            }
            setIsRequestLogOut(false);
        }
    }, [isRequestLogOut, setIsRequestLogOut, auth, authUser]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const getPageComponent = () => {
        switch (currentPageId) {
            case PAGE_ID_EMAIL_VERIFIED_SUCCESSFUL:
                return <PageMessage emailVerifySuccessful />;
            case PAGE_ID_RESEND_VERIFICATION_EMAIL:
                return <PageMessage signUpSuccessful />;
            case PAGE_ID_RESET_PASSWORD_FORM:
                return <ResetPasswordForm onSubmit={handleSubmitNewPassword} />;
            case PAGE_ID_RESET_PASSWORD_SUCCESSFUL:
                return <PageMessage passwordResetSuccessful />;
            case PAGE_ID_VERIFYING_EMAIL:
                return <Processing />;
            case PAGE_ID_ACTION_CODE_FAILED_VERIFY_EMAIL:
                return <ActionCodeFailed emailVerificationFailed />;
            case PAGE_ID_ACTION_CODE_FAILED_EMAIL_VERIFICATION:
                return <ActionCodeFailed emailVerificationFailedEmailVerified />;
            case PAGE_ID_ACTION_CODE_FAILED_VERIFIED_PWD_RESET_CODE:
                return <ActionCodeFailed passwordResetCodeVerificationFailed />;
            case PAGE_ID_REDIRECT_TO_HOME_PAGE:
                return <Redirect to={{ path: `${BASE_URL}${PATH_HOME}` }} />;
            default:
                // do nothing
                break;
        }
    };

    const handleSubmitNewPassword = async newPassword => {
        try {
            await resetPassword({
                newPassword,
                actionCode: oobCode,
            });
            logEvent(ANALYTICS_EVENT_PASSWORD_RESET);
            setCurrentPageId(PAGE_ID_RESET_PASSWORD_SUCCESSFUL);
        } catch (authError) {
            console.log({ authError });
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, scrollToPageTop, actionCode: oobCode, isRequestOpenLoginDialog, setIsRequestOpenLoginDialog }}>
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ height: '100%', width: '100%', flex: 1 }} ref={pageContentSimpleBarEl}>
                        <Grid {...defns.tempContainer}>{getPageComponent()}</Grid>
                    </SimpleBar>
                </Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default AuthHandlerLayout;
